import { useMemo } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const ICONS = {
  server: { glyph: "\uf233", color: "#8b5cf6" },      // server
  backbone: { glyph: "\uf796", color: "#0ea5e9" },    // ethernet/cable
  odc: { glyph: "\uf1e6", color: "#06b6d4" },         // plug
  odp: { glyph: "\uf0e8", color: "#f59e0b" },         // sitemap
  onu: { glyph: "\uf519", color: "#10b981" },         // house-signal
};

const pin = (type, state) => {
  const cfg = ICONS[type] || ICONS.odp;
  const ring = state === "off" ? "#ef4444" : state === "warn" ? "#f59e0b" : cfg.color;
  return L.divIcon({
    className: "",
    iconSize: [34, 34],
    iconAnchor: [17, 34],
    popupAnchor: [0, -30],
    html: `<div style="width:34px;height:34px;display:grid;place-items:center;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${cfg.color}22;border:2px solid ${ring};box-shadow:0 4px 12px #0006">
             <i class="fa-solid" style="transform:rotate(45deg);color:${ring};font-size:14px">${cfg.glyph}</i>
           </div>`,
  });
};

const stateOf = (n) =>
  n.type !== "onu" ? "ok" : !n.online ? "off" : n.signal_state === "buruk" ? "off" : n.signal_state === "waspada" ? "warn" : "ok";

/** Live map infrastruktur: marker per jenis node + jalur backbone berlabel jumlah core. */
export const InfraMap = ({ nodes }) => {
  const placed = useMemo(() => nodes.filter((n) => n.lat != null && n.lng != null), [nodes]);
  const byId = useMemo(() => Object.fromEntries(placed.map((n) => [n.id, n])), [placed]);
  const center = placed.length ? [placed[0].lat, placed[0].lng] : [-6.2, 106.816];

  const lines = placed
    .filter((n) => n.parent_id && byId[n.parent_id])
    .map((n) => ({
      id: n.id,
      from: [byId[n.parent_id].lat, byId[n.parent_id].lng],
      to: [n.lat, n.lng],
      label: `${byId[n.parent_id].name} → ${n.name}${n.cores ? ` · ${n.cores} core` : ""}`,
      color: n.type === "onu" ? "#10b981" : n.type === "backbone" ? "#0ea5e9" : "#06b6d4",
    }));

  return (
    <div className="overflow-hidden rounded-2xl border border-border" data-testid="infra-map">
      <MapContainer center={center} zoom={placed.length ? 15 : 11} style={{ height: 520, width: "100%" }} scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {lines.map((l) => (
          <Polyline key={l.id} positions={[l.from, l.to]} pathOptions={{ color: l.color, weight: 3, opacity: 0.7 }}>
            <Tooltip sticky>{l.label}</Tooltip>
          </Polyline>
        ))}
        {placed.map((n) => (
          <Marker key={n.id} position={[n.lat, n.lng]} icon={pin(n.type, stateOf(n))} data-testid={`map-marker-${n.name}`}>
            <Popup>
              <div style={{ minWidth: 170 }}>
                <b>{n.name}</b>
                <div style={{ textTransform: "uppercase", fontSize: 10, letterSpacing: 1 }}>{n.type}</div>
                {["backbone", "odc"].includes(n.type) && <div>{n.cores} core</div>}
                {["odc", "odp"].includes(n.type) && <div>Port {n.used_ports}/{n.ports}</div>}
                {n.type === "onu" && (
                  <>
                    <div>PPPoE: {n.secret_name || "-"} · {n.online ? "AKTIF" : "TIDAK AKTIF"}</div>
                    <div>Redaman: {n.rx_power != null ? `${n.rx_power} dBm` : "belum diukur"}</div>
                  </>
                )}
                {n.address && <div>{n.address}</div>}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      {!placed.length && (
        <p className="border-t border-border bg-card px-4 py-3 text-xs text-muted-foreground" data-testid="map-empty">
          Belum ada node dengan koordinat. Isi Latitude/Longitude pada node (atau tekan "Ambil lokasi saya" saat menambah dari lapangan).
        </p>
      )}
    </div>
  );
};
